import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Grid,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ExpandMore,
  Refresh,
  Delete,
  Warning,
  CheckCircle,
  Error,
  Info,
  BugReport,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { 
  clearAuthCache, 
  clearAllCache, 
  clearCookies, 
  clearIndexedDB,
  forceCleanBrowser,
  debugStorage,
  getStorageInfo,
  CACHE_KEYS 
} from '../../utils/cacheUtils';

const AuthDebugPanel = () => {
  const { 
    user, 
    loading, 
    error, 
    sessionExpired, 
    isAuthenticated, 
    refreshUser,
    clearSession 
  } = useAuth();
  
  const [storageInfo, setStorageInfo] = useState(null);
  const [lastAction, setLastAction] = useState('');

  const updateStorageInfo = () => {
    setStorageInfo(getStorageInfo());
  };

  React.useEffect(() => {
    updateStorageInfo();
  }, []);

  const handleAction = async (actionName, actionFn) => {
    try {
      await actionFn();
      setLastAction(`✅ ${actionName} completed`);
      updateStorageInfo();
    } catch (error) {
      setLastAction(`❌ ${actionName} failed: ${error.message}`);
    }
  };

  const getAuthStatus = () => {
    if (loading) return { status: 'loading', color: 'info', icon: <Info /> };
    if (error) return { status: 'error', color: 'error', icon: <Error /> };
    if (sessionExpired) return { status: 'expired', color: 'warning', icon: <Warning /> };
    if (isAuthenticated) return { status: 'authenticated', color: 'success', icon: <CheckCircle /> };
    return { status: 'unauthenticated', color: 'default', icon: <Error /> };
  };

  const authStatus = getAuthStatus();

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <BugReport color="primary" />
          <Typography variant="h5" fontWeight="bold">
            Authentication Debug Panel
          </Typography>
          <Chip 
            label={authStatus.status.toUpperCase()} 
            color={authStatus.color}
            icon={authStatus.icon}
            variant="outlined"
          />
        </Box>

        {lastAction && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {lastAction}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Auth Status */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Authentication Status
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Authenticated:</Typography>
                  <Chip 
                    label={isAuthenticated ? 'Yes' : 'No'} 
                    color={isAuthenticated ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Loading:</Typography>
                  <Chip 
                    label={loading ? 'Yes' : 'No'} 
                    color={loading ? 'warning' : 'default'}
                    size="small"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Session Expired:</Typography>
                  <Chip 
                    label={sessionExpired ? 'Yes' : 'No'} 
                    color={sessionExpired ? 'error' : 'success'}
                    size="small"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Error:</Typography>
                  <Chip 
                    label={error ? 'Yes' : 'No'} 
                    color={error ? 'error' : 'success'}
                    size="small"
                  />
                </Box>
              </Box>
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* User Info */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              {user ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>ID:</strong> {user.id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {user.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Display Name:</strong> {user.displayName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Username:</strong> {user.username}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Role:</strong> {user.role}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No user data available
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Storage Info */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">
                  Storage Information
                </Typography>
                <IconButton onClick={updateStorageInfo} size="small">
                  <Refresh />
                </IconButton>
              </Box>
              
              {storageInfo && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      LocalStorage
                    </Typography>
                    <Typography variant="body2">
                      Used: {formatBytes(storageInfo.localStorage.used)}
                    </Typography>
                    <Typography variant="body2">
                      Keys: {storageInfo.localStorage.keys.length}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {storageInfo.localStorage.keys.map(key => (
                        <Chip 
                          key={key} 
                          label={key} 
                          size="small" 
                          sx={{ mr: 0.5, mb: 0.5 }}
                          color={Object.values(CACHE_KEYS).includes(key) ? 'primary' : 'default'}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      SessionStorage
                    </Typography>
                    <Typography variant="body2">
                      Used: {formatBytes(storageInfo.sessionStorage.used)}
                    </Typography>
                    <Typography variant="body2">
                      Keys: {storageInfo.sessionStorage.keys.length}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {storageInfo.sessionStorage.keys.map(key => (
                        <Chip 
                          key={key} 
                          label={key} 
                          size="small" 
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Typography variant="h6" gutterBottom>
          Debug Actions
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleAction('Refresh User', refreshUser)}
              startIcon={<Refresh />}
            >
              Refresh User
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              onClick={() => handleAction('Clear Auth Cache', clearAuthCache)}
              startIcon={<Delete />}
            >
              Clear Auth Cache
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              onClick={() => handleAction('Clear Session', clearSession)}
              startIcon={<Delete />}
            >
              Clear Session
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={() => handleAction('Clear All Cache', clearAllCache)}
              startIcon={<Delete />}
            >
              Clear All Cache
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={() => handleAction('Clear Cookies', clearCookies)}
              startIcon={<Delete />}
            >
              Clear Cookies
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={() => handleAction('Force Clean Browser', forceCleanBrowser)}
              startIcon={<Delete />}
            >
              Force Clean All
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="text"
            onClick={() => {
              debugStorage();
              setLastAction('📊 Storage debug info logged to console');
            }}
            startIcon={<Info />}
          >
            Log Storage to Console
          </Button>
        </Box>

        {/* Cache Details Accordion */}
        <Accordion sx={{ mt: 3 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Cache Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Object.entries(CACHE_KEYS).map(([name, key]) => {
                const value = localStorage.getItem(key);
                const timestamp = localStorage.getItem(`${key}-timestamp`);
                
                return (
                  <Paper key={key} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {name} ({key})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Value: {value ? `${value.substring(0, 100)}${value.length > 100 ? '...' : ''}` : 'null'}
                    </Typography>
                    {timestamp && (
                      <Typography variant="body2" color="text.secondary">
                        Timestamp: {new Date(parseInt(timestamp)).toLocaleString()}
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Box>
  );
};

export default AuthDebugPanel;